import type { Ref, ShallowRef } from 'vue'
import type { LocationQueryRaw, RouteLocationRaw, Router } from 'vue-router'
import { sanitizeSearchQuery } from '#shared/utils/search'
import { isNavigationFailure, useRouter } from 'vue-router'
import { SEARCH_DRAFT_SETTLE_MS } from '~/constants/search'

interface ActiveTermCommit {
  value: string
  term: string
}

interface SearchRouteCommitState {
  router: Router
  draft: Ref<boolean>
  writing: Ref<boolean>
  activeTermCommit: ShallowRef<ActiveTermCommit | undefined>
  queue: Promise<void>
  settleTimer?: ReturnType<typeof setTimeout>
  draftHasCommit: boolean
}

export function useSearchRouteCommit() {
  const state = createState()
  registerLifecycle(state)

  return {
    beginDraft: () => beginDraft(state),
    submit: (value: string) => submit(state, value),
    clear: () => clear(state),
    commitTerm: (value: string) => commitTerm(state, value),
    activeTermCommit: readonly(state.activeTermCommit),
  }
}

function createState(): SearchRouteCommitState {
  return {
    router: useRouter(),
    draft: useState('search-route-draft', () => false),
    writing: useState('search-route-writing', () => false),
    activeTermCommit: shallowRef<ActiveTermCommit>(),
    queue: Promise.resolve(),
    draftHasCommit: false,
  }
}

function registerLifecycle(state: SearchRouteCommitState) {
  if (!import.meta.client) {
    return
  }

  onScopeDispose(
    state.router.afterEach((_to, _from, failure) => {
      if (!failure && !state.writing.value) {
        state.draft.value = false
      }
    }),
  )
  onScopeDispose(() => {
    if (state.settleTimer !== undefined) {
      cancelSettle(state)
      state.draft.value = false
    }
  })
}

function cancelSettle(state: SearchRouteCommitState) {
  clearTimeout(state.settleTimer)
  state.settleTimer = undefined
}

function startSettle(state: SearchRouteCommitState) {
  cancelSettle(state)
  state.settleTimer = setTimeout(() => {
    // Only ends the draft. The next keystroke pushes the new entry, since browsers may skip timer-pushed entries.
    state.settleTimer = undefined
    state.draft.value = false
  }, SEARCH_DRAFT_SETTLE_MS)
}

function enqueue(state: SearchRouteCommitState, task: () => Promise<void>) {
  const run = state.queue.then(task)
  state.queue = run.catch(() => {})
  return run
}

async function navigate(
  state: SearchRouteCommitState,
  location: RouteLocationRaw,
  mode: 'push' | 'replace',
) {
  state.writing.value = true
  try {
    return !isNavigationFailure(await state.router[mode](location))
  } catch {
    return false
  } finally {
    state.writing.value = false
  }
}

function currentSq(state: SearchRouteCommitState) {
  const sq = state.router.currentRoute.value.query.sq
  return typeof sq === 'string' ? sq : ''
}

function searchLocation(
  state: SearchRouteCommitState,
  term: string,
  resetPage: boolean,
): RouteLocationRaw {
  const query: LocationQueryRaw = {
    ...state.router.currentRoute.value.query,
    sq: term || undefined,
  }
  if (resetPage) {
    query.page = undefined
  }
  return { name: 'search-index', query }
}

async function pushDraft(state: SearchRouteCommitState) {
  const { path, query, hash } = state.router.currentRoute.value
  if (await navigate(state, { path, query, hash, force: true }, 'push')) {
    state.draft.value = true
    state.draftHasCommit = false
  }
}

// The draft entry is pushed from the keystroke itself: browsers may skip history entries pushed later from a timer.
function beginDraft(state: SearchRouteCommitState) {
  cancelSettle(state)
  return enqueue(state, async () => {
    if (
      !state.draft.value &&
      !state.writing.value &&
      state.router.currentRoute.value.name === 'search-index'
    ) {
      await pushDraft(state)
    }
    if (state.draft.value && state.draftHasCommit) {
      startSettle(state)
    }
  })
}

function commitTerm(state: SearchRouteCommitState, value: string) {
  return enqueue(state, async () => {
    const term = sanitizeSearchQuery(value)
    const previous = currentSq(state)
    if (term === previous) {
      return
    }

    const activeCommit = { value, term }
    state.activeTermCommit.value = activeCommit
    try {
      if (term === sanitizeSearchQuery(previous)) {
        await navigate(state, searchLocation(state, term, false), 'replace')
        return
      }
      const mode = state.draft.value ? 'replace' : 'push'
      if (await navigate(state, searchLocation(state, term, true), mode)) {
        state.draft.value = true
        state.draftHasCommit = true
        startSettle(state)
      }
    } finally {
      if (state.activeTermCommit.value === activeCommit) {
        state.activeTermCommit.value = undefined
      }
    }
  })
}

function submit(state: SearchRouteCommitState, value: string) {
  return enqueue(state, async () => {
    const term = sanitizeSearchQuery(value)
    const previous = currentSq(state)
    const isNewSearch = term !== sanitizeSearchQuery(previous)
    if (term !== previous || state.router.currentRoute.value.query.page !== undefined) {
      const mode = isNewSearch && !state.draft.value ? 'push' : 'replace'
      await navigate(state, searchLocation(state, term, true), mode)
    }
    state.draft.value = false
  })
}

function clear(state: SearchRouteCommitState) {
  return enqueue(state, async () => {
    cancelSettle(state)
    if (!state.draft.value) {
      await pushDraft(state)
      if (!state.draft.value) {
        return
      }
    }
    if (await navigate(state, searchLocation(state, '', true), 'replace')) {
      state.draftHasCommit = false
    }
  })
}
